import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import logoImage from '@/assets/thinkotter-logo.png';
import { reportClientEvent, routePatternFromPath } from '@/lib/errorReporter';

interface ErrorBoundaryProps {
  children?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  imgFailed: boolean;
}

/**
 * BUG-20260722-000251 (T-2, RC-001 containment): the single React class error
 * boundary in the app, mounted inside <BrowserRouter> but outside <Routes>
 * (App.tsx, T-3) so it keeps router + React Query context while catching any
 * render throw beneath it. Before this fix, ZERO boundaries existed anywhere
 * in frontend/src, so a single leaf render throw unmounted the ENTIRE app to
 * a blank white screen (RCA RC-001).
 *
 * componentDidCatch reports a LOG-ONLY 'event' (carrying the componentStack).
 * The metric-eligible 'error' send for the SAME caught render error comes
 * from main.tsx's onCaughtError root hook (T-4) — this split (ISS-003) is
 * what keeps the ThinkOtter/Client ClientError metric incrementing exactly
 * once per caught render error, so containment never reduces visibility.
 *
 * ISS-002 HARD INVARIANT: the fallback below reads ZERO hooks, ZERO context,
 * ZERO Zustand store, and ZERO react-router (no useAuth/<Link>/useNavigate) —
 * only a static <img>, plain markup, Button/Card/cn and otter-* tokens. The
 * AppLogo component is deliberately NOT reused here: it pulls in <Link> and
 * useAuth() (which runs JSON.parse(atob(idToken)) against localStorage), so
 * an auth/token-corrupting crash would make THIS OUTERMOST boundary's own
 * fallback throw a second time — a secondary crash loop with nothing left to
 * catch it. The render body is additionally wrapped in a try/catch that
 * degrades to bare text, in case an otherwise-unexpected failure occurs.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, imgFailed: false };

  static getDerivedStateFromError(): Partial<ErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    try {
      reportClientEvent('react_boundary_caught', {
        name: error?.name,
        message: error?.message,
        componentStack: info?.componentStack ?? undefined,
        routePattern: routePatternFromPath(window.location.pathname),
      });
    } catch {
      // Never let reporting itself crash the boundary (TO-LL-036 fail-safe).
    }
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  private handleImgError = (): void => {
    this.setState({ imgFailed: true });
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    try {
      return (
        <div className="flex min-h-screen items-center justify-center bg-otter-paper p-4">
          <Card className="w-full max-w-md text-center">
            <CardContent className="flex flex-col items-center gap-4 pt-6 pb-6">
              {this.state.imgFailed ? (
                <span className={cn('text-xl font-bold text-otter-earth-800')}>ThinkOtter</span>
              ) : (
                <img
                  src={logoImage}
                  alt="ThinkOtter"
                  onError={this.handleImgError}
                  className="h-10 w-auto object-contain"
                />
              )}
              <p className="text-otter-earth-800">
                Oops, something hiccuped. Your progress is saved, so let us try that again.
              </p>
              <Button variant="warm" onClick={this.handleReload}>
                Try again
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    } catch {
      // Belt-and-suspenders: if the primary fallback somehow throws, degrade to
      // bare, dependency-free text rather than leaving nothing rendered.
      return <div>ThinkOtter. Something went wrong, please reload.</div>;
    }
  }
}
